import { useEffect, useState } from 'react';
import type { Recipe, RecipePhotoRef } from '@awwyeah/shared';
import {
  isRecipePhotoFetchable,
  recipeDisplayUrl,
  recipePhotoUrl,
  recipeThumbUrl,
} from '@awwyeah/shared';
import { localRecipePhotoBlob } from '../outbox';
import { gradientClassName, placeholderArtFor } from '../utils/placeholderArt';

/**
 * Renders a recipe's header photo from whichever copy exists — same three-state shape as
 * check-in's `CheckinPhoto` (WS-13): the local outbox blob while queued, the auth'd server
 * URL once uploaded, and (recipes' own fourth state, since there's no "someone else's
 * phone" concept for a single-owner header image) the code-computed emoji-on-gradient
 * placeholder when there is no `photoRef` at all.
 */

export type RecipePhotoVariant = 'thumb' | 'display' | 'original';

function variantUrl(id: string, variant: RecipePhotoVariant): string {
  if (variant === 'thumb') return recipeThumbUrl(id);
  if (variant === 'display') return recipeDisplayUrl(id);
  return recipePhotoUrl(id);
}

export function useRecipePhotoSource(
  ref: RecipePhotoRef | undefined,
  variant: RecipePhotoVariant = 'original',
): string | null {
  const [localUrl, setLocalUrl] = useState<string | null>(null);
  const fetchable = ref ? isRecipePhotoFetchable(ref) : false;

  useEffect(() => {
    if (!ref || fetchable) return;
    let revoked = false;
    let url: string | null = null;
    void localRecipePhotoBlob(ref.id).then((blob) => {
      if (!blob || revoked) return;
      url = URL.createObjectURL(blob);
      setLocalUrl(url);
    });
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
      setLocalUrl(null);
    };
  }, [ref?.id, fetchable]);

  if (!ref) return null;
  if (fetchable) return variantUrl(ref.id, variant);
  return localUrl;
}

export function RecipePhoto({
  recipe,
  alt,
  className = '',
  variant = 'original',
}: {
  recipe: Pick<Recipe, 'id' | 'title' | 'ingredients' | 'photoRef'>;
  alt: string;
  className?: string;
  variant?: RecipePhotoVariant;
}): JSX.Element {
  const source = useRecipePhotoSource(recipe.photoRef, variant);

  if (!source) {
    const art = placeholderArtFor(recipe);
    return (
      <div
        className={`flex items-center justify-center ${gradientClassName(art.bg)} ${className}`}
        data-testid="recipe-photo-placeholder"
        aria-hidden="true"
      >
        <span className="text-4xl leading-none" role="img" aria-label={art.emoji}>
          {art.emoji}
        </span>
      </div>
    );
  }

  return (
    <img
      src={source}
      alt={alt}
      loading="lazy"
      decoding="async"
      className={`w-full object-cover ${className}`}
      data-testid="recipe-photo"
    />
  );
}
